import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { DebounceInput } from "react-debounce-input";
import InfiniteScroll from "react-infinite-scroll-component";
import cx from "classnames";

import { NavBar } from "../../commonComponents";
import { ErrorFallback, createErrorDetails } from "../../components/Error/Error";
import { MarketPlaceCard } from "../../components";
import styles from "./Marketplace.module.css";
import FleekCoin from "../../asset/icons/fleek-coin-logo.png";
import NoSearchResult from "../../asset/images/NoSearchResult.png";
import Api from "../../utils/Api/Api";
import Helper from "../../utils/Helper";
import { money } from "../../utils/format";
import { marketCurrency, isIndia, noteStoreCurrency } from "../../utils/market";
import { parseMoneyLabel, currencyCode } from "../../utils/currency";
import { copyFor } from "./copy";
import { track, EVENTS } from "../../utils/analyticsService";
import { useNoResultsCapture } from "../../utils/demandCapture";
import { BrandRequest } from "./BrandRequest";
import { MarketSwitcher } from "../../components/MarketSwitcher/MarketSwitcher";
import { useSeo } from "../../utils/useSeo";
import { storeHomeMeta } from "../../seo/storeHome";
import { storeLinkProps, STORES, PLACEMENTS } from "../../utils/storeLinks";
import { useAppBarShowing } from "../../utils/appBanner";
import { loadBestsellers, orderLeadBrands, badgeRank, shouldShowStrip } from "../../utils/bestsellers";
import { BestsellersPanel, panelFor } from "./BestsellersPanel";
import { exampleBrandNames } from "./heroBrands";

/* ------------------------------------------------------------------
   Helpers
   ------------------------------------------------------------------ */

// `starts_from` arrives pre-formatted from the API, e.g. "₹649/6 mo".
const parsePrice = (startsFrom) => {
  if (!startsFrom) return null;
  const match = String(startsFrom).match(/([\d,]+(?:\.\d+)?)/);
  return match ? Number(match[1].replace(/,/g, "")) : null;
};

// The currency a listing is priced in, read off its `starts_from` labels
// ("$175.99/12 mo", "€9/mo", "CHF 12/mo"), or null when none says.
const listingCurrency = (brands) => {
  for (const b of brands || []) {
    const parsed = parseMoneyLabel(b?.starts_from);
    if (parsed) return parsed.currency;
  }
  return null;
};

// Prices are in the store's currency (rupees in India; dollars, or the
// store's own currency, elsewhere).
const formatINR = (value) =>
  typeof value === "number" && !Number.isNaN(value) ? money(value, marketCurrency()) : "";


// App store ratings, read from the live listings on 2026-09-07.
//   Google Play : 4.4 from 6,820 ratings  (com.fleek.app)
//   App Store   : 3.9 from   305 ratings  (id1604744564)
// Combined as a weighted mean over all 7,125 ratings:
//   (4.4*6820 + 3.9*305) / 7125 = 4.38 -> 4.4
// Shown on the page as well as in JSON-LD: Google suppresses rich results for
// ratings that are not visible to the user.
const APP_RATING = { value: 4.4, count: 7125 };

// Brand pinned to the hero deal card; falls back to the biggest discount
// when it isn't in the loaded list (e.g. during a search).

// The most popular brands, in curated order. Floated to the front of the
// "This week's best prices" grid (All view) so the first cards a visitor sees
// are the household names; the rest fill in behind them by discount. When the
// live bestsellers load they lead instead, in rank order, then any Fleek picks
// (unbadged), and this list follows them; if they don't, this is the whole lead.
const POPULAR_BRAND_PATHS = [
  "amazon-prime",
  "z5",
  "microsoft",
  "xbox",
  "sonyliv",
  "chatgpt",
  "uber",
  "zomato",
  "nykaa",
  "mcafee",
];

// Illustrative marketing content, deliberately not live data.
const APP_PREVIEW_ROWS = [
  { name: "Netflix", meta: "Renews 12 Mar · ₹649", on: true },
  { name: "Spotify", meta: "Renews 18 Mar · ₹119", on: true },
  { name: "Audible", meta: "Unused for 3 months", on: false },
];

/* ------------------------------------------------------------------
   Small presentational pieces
   ------------------------------------------------------------------ */

const SearchIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="11" cy="11" r="8" />
    <path d="m21 21-4.3-4.3" />
  </svg>
);

const HeartIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" />
  </svg>
);

const ShieldIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M20 13c0 5-3.5 7.5-8 9-4.5-1.5-8-4-8-9V6l8-3 8 3Z" />
    <path d="m9 12 2 2 4-4" />
  </svg>
);

// Brand logos come from the API and occasionally point at a CDN that no longer
// serves them (the dev API still returns dead Cloudinary URLs). Falling back to a
// monogram keeps the grid intact instead of showing broken-image icons.
const BrandLogo = ({ src, name, className, size }) => {
  const [failed, setFailed] = useState(false);
  const label = (name || "").trim();

  if (!src || failed) {
    return (
      <span
        className={cx(className, styles.logoFallback)}
        role="img"
        aria-label={label || "Brand logo unavailable"}
      >
        {label ? label.charAt(0).toUpperCase() : "?"}
      </span>
    );
  }

  return (
    <img
      className={className}
      src={src}
      alt={label}
      width={size}
      height={size}
      loading="lazy"
      onError={() => setFailed(true)}
    />
  );
};

const OfferSkeleton = () => (
  <div className={styles.skeletonGrid}>
    {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
      <div className={styles.skeletonCard} key={i}>
        <div className={cx(styles.shimmer, styles.skelLogo)} />
        <div className={cx(styles.shimmer, styles.skelLine)} />
        <div className={cx(styles.shimmer, styles.skelLineShort)} />
        <div className={cx(styles.shimmer, styles.skelPrice)} />
        <div className={cx(styles.shimmer, styles.skelBtn)} />
      </div>
    ))}
  </div>
);

/* ------------------------------------------------------------------
   Page
   ------------------------------------------------------------------ */

// A store home's title and description as the prerender built them (it saw
// the store's whole catalogue); without one, the store's generic words.
const readStoreHome = (country, currency) => {
  try {
    const el = typeof document !== "undefined" && document.getElementById("store-home-seed");
    const seed = el ? JSON.parse(el.textContent || "null") : null;
    if (seed && seed.country === country) return seed;
  } catch {
    /* no seed: fall through */
  }
  return storeHomeMeta(country, { currency });
};

/**
 * The marketplace: India's home at / and /marketplace, and a country store's
 * home at /us (`country` "US"), which has its own address, title and canonical.
 */
export const Marketplace = ({ isLoggedIn, country = "" }) => {
  const navigate = useNavigate();
  const isMobile = Helper.isMobile();

  const [brandData, setBrandData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasError, setError] = useState(false);
  const [errorDetails, setErrorDetails] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [searchValue, setSearchValue] = useState("");
  const [activeCategory, setActiveCategory] = useState("All");
  const [openFaq, setOpenFaq] = useState(0);
  // The query a brand request was sent for, so both empty states thank once.
  const [requestedFor, setRequestedFor] = useState(null);
  // The query whose first page genuinely came back empty. A failed request
  // resolves here too (the app-wide interceptor swallows errors), and must
  // not count as "nobody sells this".
  const [emptyFor, setEmptyFor] = useState(null);
  // How many brands the whole store lists: the unsearched listing's count.
  // Null until it arrives (or if it never does): the hero then shows no
  // "N brands" stat rather than counting the page loaded so far.
  const [storeBrandCount, setStoreBrandCount] = useState(null);
  // Live bestsellers, ranked. Empty until (and unless) they load.
  const [bestsellers, setBestsellers] = useState([]);

  // The store's categories, counted over its whole brand list
  // (marketplace/v2/store_categories). Null until loaded, or if it fails:
  // the page then counts the brands it has, as before.
  const [storeCategories, setStoreCategories] = useState(null);
  // A picked category's brands, all of them, by category name: its deals rank
  // the whole category, not the part of it the grid has loaded so far.
  const [categoryBrands, setCategoryBrands] = useState({});

  // Keeps the paging effect from re-firing on its own state writes.
  const pageRef = useRef(1);

  /* ---------------- data ---------------- */

  const fetchBrands = useCallback(
    async ({ pageNum, query, replace }) => {
      try {
        const { data } = await Api.MarketPlaceData({
          service_name: query || undefined,
          page: pageNum,
          digital_only: 1,
          page_size: 30,
        });

        const results = data?.results || [];
        if (pageNum === 1 && !query && Number.isFinite(data?.count)) setStoreBrandCount(data.count);
        if (pageNum === 1) {
          setEmptyFor(Array.isArray(data?.results) && results.length === 0 ? query || "" : null);
        }
        if (query && pageNum === 1) {
          // Length only: a typed query can carry personal data.
          track(EVENTS.SEARCH_PERFORMED, { query_length: String(query).length, results_count: data?.count ?? results.length });
        }
        setHasMore(Boolean(data?.next));
        // The store picker and notice say the currency these prices are in.
        noteStoreCurrency(listingCurrency(results));
        setBrandData((prev) => (replace ? results : [...prev, ...results]));
      } catch (err) {
        setError(true);
        setErrorDetails(
          createErrorDetails(err, {
            componentName: "Marketplace",
            errorContext: "marketplace_data_fetch",
            apiCallsAttempted: ["MarketPlaceData"],
            additionalContext: {
              user_logged_in: isLoggedIn,
              brands_count: brandData?.length || 0,
            },
          })
        );
      } finally {
        setLoading(false);
      }
    },
    // brandData is only read for error context; excluding it keeps this stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isLoggedIn]
  );

  // Bestsellers load beside the vendor list and never hold the page up: until
  // they arrive (or if they fail) the strip is absent and the grid keeps its
  // curated order.
  useEffect(() => {
    let cancelled = false;
    loadBestsellers().then((list) => {
      // Too few to show the strip is too few to reorder or badge by, too.
      if (!cancelled) setBestsellers(shouldShowStrip(list) ? list : []);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await Api.StoreCategories();
        const list = res?.data?.categories;
        if (!cancelled && Array.isArray(list) && list.length) setStoreCategories(list);
      } catch (e) {
        /* the counted-from-loaded-brands fallback stays */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // A picked category's brands, every page of them, once per category.
  useEffect(() => {
    if (activeCategory === "All" || categoryBrands[activeCategory]) return undefined;
    let cancelled = false;
    (async () => {
      let all = [];
      try {
        for (let pageNum = 1; pageNum <= 10; pageNum += 1) {
          const res = await Api.MarketPlaceData({ category: activeCategory, digital_only: 1, page_size: 100, page: pageNum });
          const results = res?.data?.results;
          if (!Array.isArray(results)) break;
          all = all.concat(results);
          if (!res.data.next) break;
        }
      } catch (e) {
        /* whatever arrived is still better than the loaded page alone */
      }
      if (!cancelled && all.length) setCategoryBrands((prev) => ({ ...prev, [activeCategory]: all }));
    })();
    return () => {
      cancelled = true;
    };
  }, [activeCategory, categoryBrands]);

  // catalogue_viewed on arrival and on every category switch.
  useEffect(() => {
    track(EVENTS.CATALOGUE_VIEWED, { category: activeCategory, context: "marketplace" });
  }, [activeCategory]);

  // Search (and first load). Resets paging.
  useEffect(() => {
    setLoading(true);
    pageRef.current = 1;
    setPage(1);
    fetchBrands({ pageNum: 1, query: searchValue, replace: true });
  }, [searchValue, fetchBrands]);

  const loadMore = () => {
    const next = pageRef.current + 1;
    pageRef.current = next;
    setPage(next);
    fetchBrands({ pageNum: next, query: searchValue, replace: false });
  };

  /* ---------------- derived ---------------- */

  // A search that found nothing at all, as opposed to a category with no deals.
  const noResults =
    Boolean(searchValue.trim()) && !loading && emptyFor === searchValue && brandData.length === 0;
  useNoResultsCapture(searchValue, noResults);

  const categories = useMemo(() => {
    if (storeCategories) {
      return storeCategories.map((c) => ({ name: c.name, count: c.count, logos: (c.logos || []).slice(0, 3) }));
    }
    const map = new Map();
    brandData.forEach((b) => {
      const name = b?.service?.category;
      if (!name) return;
      if (!map.has(name)) map.set(name, { name, count: 0, logos: [] });
      const entry = map.get(name);
      entry.count += 1;
      if (entry.logos.length < 3 && b?.service?.logo_url) {
        entry.logos.push(b.service.logo_url);
      }
    });
    return [...map.values()].sort((a, b) => b.count - a.count);
  }, [brandData, storeCategories]);

  const deals = useMemo(() => {
    // A picked category ranks all its brands once they have arrived; until
    // then, the ones already loaded.
    const source = activeCategory !== "All" && categoryBrands[activeCategory] ? categoryBrands[activeCategory] : brandData;
    const byDiscount = source
      .filter((b) => b?.service?.category === activeCategory || activeCategory === "All")
      .filter((b) => b.discount_percentage)
      .sort((a, b) => (b.discount_percentage || 0) - (a.discount_percentage || 0));

    // Category views stay purely discount-ranked. The All view leads with the
    // bestsellers (rank order), then Fleek picks, then the curated popular set (in order,
    // discount or not), then fills the remaining slots with the biggest
    // remaining discounts.
    if (activeCategory !== "All") return byDiscount.slice(0, 12);

    return orderLeadBrands({
      brands: brandData,
      rest: byDiscount,
      bestsellers,
      curatedPaths: POPULAR_BRAND_PATHS,
    }).slice(0, 12);
  }, [brandData, activeCategory, bestsellers, categoryBrands]);

  // The hero's right-hand panel: bestsellers, or this store's top deals where
  // nothing has sold yet. Replaces "Deal of the day" and its daily countdown.
  const panel = useMemo(() => panelFor({ bestsellers, brands: brandData }), [bestsellers, brandData]);
  const lead = panel?.items?.[0] || null;

  // The page's words for this store (./copy.js): India's as always; abroad,
  // only brands and discounts this store actually has.
  const india = isIndia();
  // The Android "get the app" bar is already on screen: one app pitch per page.
  const appBarShowing = useAppBarShowing();
  // "Up to N% off" is a discount the hero itself shows: the best one in its
  // panel (subscriptions first, like the panel). With no panel, the store's
  // best, which the grid below then carries.
  const heroDiscount = useMemo(() => {
    const shown = panel ? panel.items : brandData;
    return Math.max(0, ...shown.map((b) => Number(b.discount ?? b.discount_percentage) || 0));
  }, [panel, brandData]);
  const copy = useMemo(
    () =>
      copyFor({
        india,
        brands: brandData.map((b) => b?.service?.service_name),
        examples: exampleBrandNames(brandData),
        brandCount: storeBrandCount,
        maxDiscount: heroDiscount,
      }),
    [india, brandData, storeBrandCount, heroDiscount]
  );
  const FAQ_DATA = copy.faq;

  const wallBrands = useMemo(
    () => brandData.filter((b) => b?.service?.logo_url).slice(0, 24),
    [brandData]
  );

  /* ---------------- handlers ---------------- */

  const selectCategory = (name) => {
    setActiveCategory(name);
    if (searchValue) setSearchValue("");
  };

  const resetFilters = () => {
    setSearchValue("");
    setActiveCategory("All");
  };

  const goToBrand = (brand) => {
    if (brand?.vendor_web_path) navigate(`/brand/${brand.vendor_web_path}`);
  };

  // A store's currency: what its listing is priced in, else what its prices
  // last came back in, else the picker's label ("USD").
  const storeCurrency = useMemo(
    () => (country ? currencyCode(listingCurrency(brandData) || marketCurrency(country)) : null),
    [country, brandData]
  );
  const storeHome = useMemo(() => (country ? readStoreHome(country, storeCurrency) : null), [country, storeCurrency]);

  useSeo({
    title: storeHome ? storeHome.title : "Fleek — Buy subscriptions for less | OTT, security, news & more",
    description: storeHome
      ? storeHome.description
      : "Compare and buy subscriptions at up to 66% off. Amazon Prime, Sony LIV, " +
        "Microsoft 365, Kaspersky, Livemint and more — verified sellers, instant " +
        "delivery, coins back on every order.",
    // / and /marketplace are one page; / is the canonical (it holds the clicks).
    // A store's home is its own page.
    canonical: storeHome ? storeHome.url : "https://web.getfleek.app/",
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        // Who Fleek is (the prerendered home page carries the same two nodes).
        {
          "@type": "Organization",
          "@id": "https://web.getfleek.app/#organization",
          name: "Fleek",
          url: "https://getfleek.app",
          logo: "https://web.getfleek.app/fleeklogo.png",
          sameAs: [
            "https://play.google.com/store/apps/details?id=com.fleek.app",
            "https://apps.apple.com/in/app/id1604744564",
            "https://www.instagram.com/getfleekapp/",
            "https://www.facebook.com/getfleekdotapp",
            "https://mobile.twitter.com/getfleekdotapp",
          ],
        },
        {
          "@type": "WebSite",
          "@id": "https://web.getfleek.app/#website",
          url: "https://web.getfleek.app/",
          name: "Fleek",
          publisher: { "@id": "https://web.getfleek.app/#organization" },
        },
        {
          "@type": "FAQPage",
          mainEntity: FAQ_DATA.map((item) => ({
            "@type": "Question",
            name: item.q,
            acceptedAnswer: { "@type": "Answer", text: item.a },
          })),
        },
        {
          "@type": "SoftwareApplication",
          name: "Fleek — Buy & Track Subscriptions",
          applicationCategory: "FinanceApplication",
          operatingSystem: "Android, iOS",
          offers: { "@type": "Offer", price: "0", priceCurrency: "INR" },
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: APP_RATING.value,
            ratingCount: APP_RATING.count,
            bestRating: 5,
            worstRating: 1,
          },
        },
      ],
    },
  });

  if (hasError) return <ErrorFallback errorDetails={errorDetails} />;

  return (
    <div className={styles.page}>
      {isMobile && <NavBar isLoggedIn={isLoggedIn} landing />}

      {/* ---------- Hero ---------- */}
      <section className={styles.hero}>
        <div className={styles.heroGlow} />
        <div className={styles.heroInner}>
          <div className={styles.heroLeft}>
            <h1 className={styles.h1}>
              Every subscription you want.
              <br />
              <span className={styles.h1Accent}>{copy.accent}</span>
            </h1>
            <p className={styles.heroSub}>
              {copy.sub}
            </p>

            <div className={styles.heroSearch}>
              <SearchIcon className={styles.heroSearchIcon} />
              <DebounceInput
                className={styles.heroSearchInput}
                debounceTimeout={300}
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                placeholder="Search your favourite brands"
                aria-label="Search your favourite brands"
              />
              <button
                type="button"
                className={cx(styles.btn, styles.btnMd, styles.btnInk)}
                onClick={() => {
                  const el = document.getElementById("deals");
                  if (el) el.scrollIntoView({ behavior: "smooth" });
                }}
              >
                Search
              </button>
            </div>

            <div className={styles.statsRow}>
              {copy.stats.map((stat) => (
                <div key={stat.label}>
                  <div className={styles.statValue}>{stat.value}</div>
                  <div className={styles.statLabel}>{stat.label}</div>
                </div>
              ))}
            </div>
          </div>

          {panel && (
            <div className={styles.heroRight}>
              <BestsellersPanel
                panel={panel}
                footer={
                  <>
                    <ShieldIcon />
                    {copy.dealTrust}
                  </>
                }
              />
            </div>
          )}
        </div>
      </section>

      {/* ---------- Best prices ---------- */}
      <section className={cx(styles.shell, styles.section)} id="deals">
        <div className={styles.sectionHead}>
          <div>
            <div className={styles.eyebrow}>Biggest discounts</div>
            <h2 className={styles.h2}>This week's best prices</h2>
          </div>
          <div className={styles.sectionMeta}>
            {deals.length} plans shown{copy.pricesNote ? ` · ${copy.pricesNote}` : ""}
          </div>
        </div>

        <div className={styles.chipRow}>
          {["All", ...categories.map((c) => c.name)].map((name) => (
            <button
              key={name}
              type="button"
              className={cx(styles.chip, activeCategory === name && styles.chipActive)}
              onClick={() => selectCategory(name)}
            >
              {name}
            </button>
          ))}
        </div>

        {loading ? (
          <OfferSkeleton />
        ) : deals.length ? (
          <div className={styles.dealsGrid}>
            {deals.map((brand) => {
              const price = parsePrice(brand.starts_from);
              const rank = badgeRank(bestsellers, {
                vendorId: brand.vendor_id,
                vendorWebPath: brand.vendor_web_path,
              });
              const open = () => {
                if (rank) {
                  track(EVENTS.BESTSELLER_CLICKED, {
                    rank,
                    kind: "bestseller",
                    vendor_id: brand.vendor_id,
                    where: "grid_badge",
                  });
                }
                goToBrand(brand);
              };
              return (
                <div className={styles.offerCard} key={brand.vendor_id}>
                  <div className={styles.offerImageWrap}>
                    <BrandLogo
                      className={styles.offerImage}
                      src={brand.service?.logo_url}
                      name={brand.service?.service_name}
                      size="160"
                    />
                    {rank ? (
                      <span className={styles.bestsellerBadge}>Bestseller</span>
                    ) : null}
                    {brand.discount_percentage ? (
                      <span className={styles.offerBadge}>
                        <span className={styles.offerBadgeText}>
                          UPTO {brand.discount_percentage}% off
                        </span>
                        <img className={styles.offerCoin} src={FleekCoin} alt="" />
                      </span>
                    ) : null}
                  </div>

                  <div className={styles.offerBody}>
                    <div className={styles.offerTop}>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div className={styles.offerBrand}>
                          {brand.service?.service_name}
                        </div>
                        <div className={styles.offerCat}>
                          {brand.service?.category}
                        </div>
                      </div>
                    </div>

                    <div className={styles.offerPriceRow}>
                      {/* The design renders starts_from verbatim here - "₹649/6 mo" -
                          so the duration stays with the price. The hero deal card is
                          the opposite: bare number, duration in the line beneath. */}
                      <span className={styles.offerPrice}>
                        {brand.starts_from || formatINR(price)}
                      </span>
                      {brand.mrp ? (
                        <span className={styles.offerMrp}>
                          {formatINR(Number(brand.mrp))}
                        </span>
                      ) : null}
                    </div>

                    <div className={styles.offerSpacer} />

                    <div className={styles.offerActions}>
                      <button
                        type="button"
                        className={cx(styles.btn, styles.btnSm, styles.btnInk, styles.btnBlock)}
                        onClick={open}
                      >
                        View plans
                      </button>
                      <button
                        type="button"
                        className={styles.wishBtn}
                        aria-label={`Save ${brand.service?.service_name} for later`}
                      >
                        <HeartIcon />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className={styles.emptyState}>
            <img className={styles.emptyImage} src={NoSearchResult} alt="" />
            <div className={styles.emptyTitle}>No results found!</div>
            <div className={styles.emptyBody}>
              Try a different brand, or browse everything we have.
            </div>
            {noResults && (
              <BrandRequest
                key={searchValue}
                query={searchValue.trim()}
                sent={requestedFor === searchValue}
                onSent={() => setRequestedFor(searchValue)}
              />
            )}
            <button type="button" className={styles.exploreBtn} onClick={resetFilters}>
              Explore marketplace
            </button>
          </div>
        )}
      </section>

      {/* ---------- Browse by category ---------- */}
      <section className={cx(styles.shell, styles.section)} id="categories">
        <div className={styles.sectionHead}>
          <div>
            <h2 className={styles.h2}>Browse by category</h2>
            <p className={styles.sectionSub}>
              Everything you subscribe to, in one marketplace.
            </p>
          </div>
        </div>

        <div className={styles.categoryGrid}>
          {categories.map((cat) => (
            <button
              type="button"
              key={cat.name}
              className={styles.categoryTile}
              onClick={() => {
                selectCategory(cat.name);
                const el = document.getElementById("deals");
                if (el) el.scrollIntoView({ behavior: "smooth" });
              }}
            >
              <span className={styles.categoryChips}>
                {cat.logos.map((logo, i) => (
                  <img
                    key={i}
                    className={styles.categoryChipImg}
                    src={logo}
                    alt=""
                    width="38"
                    height="38"
                    loading="lazy"
                    onError={(e) => { e.currentTarget.style.visibility = "hidden"; }}
                  />
                ))}
              </span>
              <span className={styles.categoryName}>{cat.name}</span>
              <span className={styles.categoryCount}>
                {cat.count} {cat.count === 1 ? "brand" : "brands"}
              </span>
            </button>
          ))}
        </div>
      </section>

      {/* ---------- Explore all brands ---------- */}
      <section className={cx(styles.shell, styles.section)} id="brands">
        <div className={styles.sectionHead}>
          <h2 className={styles.h2}>Explore all brands</h2>
          <div className={styles.brandSearch}>
            <SearchIcon className={styles.heroSearchIcon} />
            <DebounceInput
              className={styles.brandSearchInput}
              debounceTimeout={300}
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              placeholder="Search brands"
              aria-label="Search brands"
            />
          </div>
        </div>

        {loading ? (
          <OfferSkeleton />
        ) : noResults ? (
          <div className={styles.emptyState}>
            <div className={styles.emptyBody}>No brands match "{searchValue.trim()}".</div>
            <BrandRequest
              key={searchValue}
              query={searchValue.trim()}
              sent={requestedFor === searchValue}
              onSent={() => setRequestedFor(searchValue)}
            />
          </div>
        ) : (
          <InfiniteScroll
            dataLength={brandData.length}
            next={loadMore}
            hasMore={hasMore}
            loader={null}
            style={{ overflow: "visible" }}
            endMessage={
              <p className={styles.endMessage}>That's all the brands we have!</p>
            }
          >
            <div className={styles.brandGrid}>
              {brandData.map((brand, index) => (
                <MarketPlaceCard key={`${brand.vendor_id}-${index}`} data={brand} />
              ))}
            </div>
          </InfiniteScroll>
        )}
      </section>

      {/* ---------- Bundles ---------- */}
      <section className={cx(styles.shell, styles.sectionWide)} id="bundles">
        <div className={styles.bundlePanel}>
          <span className={styles.comingSoonPill}>Coming soon</span>
          <h2 className={styles.h2}>Exclusive bundles on Fleek</h2>
          <p className={styles.bundleBody}>
            Several brands, one price — buy more for less. We're putting the first bundles
            together now.
          </p>
          <div style={{ display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap" }}>
            <button
              type="button"
              className={cx(styles.btn, styles.btnMd, styles.btnSecondary)}
            >
              Notify me
            </button>
            <button
              type="button"
              className={styles.textLink}
              onClick={() => {
                const el = document.getElementById("deals");
                if (el) el.scrollIntoView({ behavior: "smooth" });
              }}
            >
              Browse best prices instead
            </button>
          </div>
        </div>
      </section>

      {/* The brand wall is India's catalogue. */}
      {india && (
        <>
      {/* ---------- Brand wall ---------- */}
      <section className={cx(styles.shell, styles.section)}>
        <div className={styles.brandWall}>
          <div className={styles.brandWallHead}>
            <h3 className={styles.h3}>Brands on Fleek</h3>
            <span className={styles.brandWallNote}>and new ones every week</span>
          </div>
          <div className={styles.brandWallGrid}>
            {wallBrands.map((brand) => (
              <div
                className={styles.brandWallCell}
                key={brand.vendor_id}
                title={brand.service?.service_name}
              >
                <BrandLogo
                  className={styles.brandWallLogo}
                  src={brand.service?.logo_url}
                  name={brand.service?.service_name}
                />
              </div>
            ))}
          </div>
        </div>
      </section>

        </>
      )}

      {/* ---------- How it works ---------- */}
      <section className={cx(styles.shell, styles.sectionWide)} id="how">
        <div className={styles.sectionHead}>
          <h2 className={styles.h2}>Three taps, done</h2>
        </div>
        <div className={styles.howGrid}>
          {copy.steps.map((step, i) => (
            <div className={styles.howCard} key={step.title}>
              <div className={styles.howStep}>{i + 1}</div>
              <div className={styles.howTitle}>{step.title}</div>
              <p className={styles.howBody}>{step.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* The app is listed in India's stores only, with rupee examples.
          Not while the Android app bar (components/AppBanner) is showing. */}
      {india && !appBarShowing && (
        <>
      {/* ---------- App banner ---------- */}
      <section className={cx(styles.shell, styles.sectionWide)} id="app">
        <div className={styles.appBanner}>
          <div className={styles.appGlow} />
          <div className={styles.appLeft}>
            <h3 className={styles.appTitle}>Bought it here? Manage it in the app.</h3>
            <p className={styles.appBody}>
              Fleek finds every subscription hiding in your bank and email, warns you before
              renewals, and cancels the ones you forgot with a single toggle.
            </p>
            <div className={styles.appRating}>
              <span className={styles.appRatingStars} aria-hidden="true">★</span>
              <span>
                {APP_RATING.value} · {APP_RATING.count.toLocaleString("en-IN")} ratings
                on Google Play and the App Store
              </span>
            </div>

            <div className={styles.appButtons}>
              <a
                className={cx(styles.btn, styles.btnLg, styles.btnInk)}
                {...storeLinkProps(STORES.APP_STORE, PLACEMENTS.MARKETPLACE)}
                target="_blank"
                rel="noreferrer"
              >
                App Store
              </a>
              <a
                className={cx(styles.btn, styles.btnLg, styles.btnSecondary)}
                {...storeLinkProps(STORES.PLAY, PLACEMENTS.MARKETPLACE)}
                target="_blank"
                rel="noreferrer"
              >
                Google Play
              </a>
            </div>
          </div>

          <div className={styles.appCard}>
            <div className={styles.appCardLabel}>Your subscriptions</div>
            {APP_PREVIEW_ROWS.map((row) => (
              <div className={styles.appRow} key={row.name}>
                <div className={styles.appMonogram}>{row.name.charAt(0)}</div>
                <div style={{ minWidth: 0 }}>
                  <div className={styles.appRowName}>{row.name}</div>
                  <div className={styles.appRowMeta}>{row.meta}</div>
                </div>
                <span className={cx(styles.switch, row.on && styles.switchOn)}>
                  <span className={styles.switchKnob} />
                </span>
              </div>
            ))}
            <div className={styles.appCardFoot}>
              <span className={styles.appFootLabel}>Unused this month</span>
              <span className={styles.appFootValue}>₹848</span>
            </div>
          </div>
        </div>
      </section>

        </>
      )}

      {/* Country store picker: only once markets are on (mobile has no footer). */}
      <div className={styles.marketRow}>
        <MarketSwitcher />
        <a className={styles.allStores} href="/stores">All stores</a>
      </div>

      {/* ---------- FAQ ---------- */}
      <section className={styles.faqWrap}>
        <h2 className={styles.h2}>Questions, answered</h2>
        <div className={styles.faqList}>
          {FAQ_DATA.map((item, i) => (
            <div className={styles.faqItem} key={item.q}>
              <button
                type="button"
                className={styles.faqTrigger}
                aria-expanded={openFaq === i}
                onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
              >
                {item.q}
                <span className={styles.faqIndicator} aria-hidden="true">
                  {openFaq === i ? "−" : "+"}
                </span>
              </button>
              {openFaq === i && <div className={styles.faqAnswer}>{item.a}</div>}
            </div>
          ))}
        </div>
      </section>

      {/* ---------- Mobile sticky buy bar ---------- */}
      {isMobile && lead && (
        <div className={styles.buyBar}>
          <div>
            <div className={styles.buyBarLabel}>{panel.kind === "bestsellers" ? "Bestseller #1" : "Top deal"}</div>
            <div className={styles.buyBarPrice}>
              {lead.name}{lead.price ? ` · from ${lead.price}` : ""}
            </div>
          </div>
          <button
            type="button"
            className={cx(styles.btn, styles.btnMd, styles.btnInk, styles.buyBarCta)}
            onClick={() => navigate(`/brand/${lead.path}`)}
          >
            View plans
          </button>
        </div>
      )}

    </div>
  );
};
